import { Box, DrawerSlotsAndSlotProps, Grid } from '@mui/joy';
import { t } from 'i18next';
import {
  Children,
  CSSProperties,
  Dispatch,
  Fragment,
  isValidElement,
  MouseEvent,
  ReactNode,
  RefObject,
  SetStateAction,
} from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form';

import { Button, SlidingPanel, SlidingPanelContent } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { ISubmitAction } from 'core/types';
import { isEmptyArray, keepFocusOnMouseDown, markSubmitIntent } from 'core/utils';
import { Color, Size, Variant } from 'theme/enums';

import FieldChild from './FieldChild';
import { ContentWrapper, FieldWrapper } from './style';
import { getDefaultSubmitAction } from './utils';

interface IProps {
  title?: string;
  subtitle?: string | JSX.Element;
  size?: Size;
  useGridLayout: boolean;
  isOpen?: boolean;
  onSubmit?: (
    reset: UseFormReset<FieldValues>,
    setError: Dispatch<SetStateAction<string>>
  ) => (data: FieldValues) => void;
  onClose: (reset?: UseFormReset<FieldValues>) => void;
  onRemove?: (reset?: UseFormReset<FieldValues>) => void;
  submitBtnLabel?: string;
  cancelBtnLabel?: string;
  removeBtnLabel?: string;
  extraButtons?: ReactNode | ReactNode[];
  disabledSubmit?: boolean;
  disabledCancel?: boolean;
  disableAutoClose?: boolean;
  allowRemove?: boolean;
  children?: ReactNode | ReactNode[];
  control?: Control<FieldValues, object>;
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  isSubmitting?: boolean;
  setValue?: UseFormSetValue<FieldValues>;
  getValues?: UseFormGetValues<FieldValues>;
  reset?: UseFormReset<FieldValues>;
  errors?: FieldErrors<FieldValues>;
  setError?: Dispatch<SetStateAction<string>>;
  getSubmitActions?: () => ISubmitAction[];
  notificationPosition?: NotificationPosition;
  withoutNotification?: boolean;
  interactive: boolean;
  inline: boolean;
  withoutButtons: boolean;
  drawerSlotAndSlotProps?: DrawerSlotsAndSlotProps;
  level?: number;
  stickySxProps?: {
    header?: CSSProperties;
    notification?: CSSProperties;
  };
  fieldWrapperSxProps?: CSSProperties;
  shouldSubmitOnKeyDown?: boolean;
  contentWrapperRef: RefObject<HTMLDivElement>;
}

const FormView = ({
  title,
  subtitle,
  size,
  useGridLayout,
  isOpen,
  onSubmit,
  onClose,
  onRemove,
  submitBtnLabel = t('buttonLabelSaveChanges'),
  cancelBtnLabel = t('buttonLabelDiscardChanges'),
  removeBtnLabel = t('buttonLabelRemove'),
  extraButtons,
  disabledSubmit = false,
  disabledCancel = false,
  disableAutoClose = false,
  allowRemove = false,
  children,
  control,
  handleSubmit,
  isSubmitting,
  setValue,
  getValues,
  reset,
  errors,
  setError,
  getSubmitActions,
  notificationPosition,
  withoutNotification,
  interactive,
  inline,
  withoutButtons,
  drawerSlotAndSlotProps,
  level = 0,
  stickySxProps,
  fieldWrapperSxProps,
  shouldSubmitOnKeyDown = true,
  contentWrapperRef,
}: IProps) => {
  const hasChildren = Boolean(children) && !isEmptyArray(children);

  const onSubmitAction =
    onSubmit || getDefaultSubmitAction(getSubmitActions?.() ?? [], disabledSubmit)?.onSubmit;

  const renderChildren = (children: ReactNode | ReactNode[]) => {
    return Children.map(children, (child: ReactNode) => {
      if (isValidElement(child)) {
        if (child.type === Fragment && child?.props?.children) {
          return renderChildren(child.props.children);
        }

        return child.props?.name ? (
          <FieldChild
            child={child}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onSubmit={onSubmitAction}
            control={control}
            setValue={setValue}
            getValues={getValues}
            errors={errors}
            reset={reset}
            setError={setError}
          />
        ) : (
          child
        );
      }

      return child;
    });
  };

  const submitButtonProps = shouldSubmitOnKeyDown
    ? {
        onMouseDown: (event: MouseEvent<HTMLElement>) => {
          keepFocusOnMouseDown(event);
          markSubmitIntent();
          contentWrapperRef.current.scrollIntoView();
          handleSubmit(onSubmit(reset, setError))();
        },
      }
    : {
        // The submit fires on click, but the field blur fires at mousedown — mark intent
        // there so the blur's pricing preview is suppressed before the click lands.
        onMouseDown: markSubmitIntent,
        onClick: () => {
          contentWrapperRef.current.scrollIntoView();
          handleSubmit(onSubmit(reset, setError))();
        },
      };

  const buttons = withoutButtons ? null : (
    <>
      {onSubmit && (
        <Button
          text={submitBtnLabel}
          data-suppress-blur-preview
          {...submitButtonProps}
          disabled={disabledSubmit}
        />
      )}

      <Box sx={{ display: 'flex', gap: 'inherit' }}>
        {getSubmitActions &&
          getSubmitActions().map((action, index) => {
            if (action?.hide) {
              return null;
            }

            const submitButtonProps = shouldSubmitOnKeyDown
              ? {
                  onMouseDown: (event: MouseEvent<HTMLElement>) => {
                    keepFocusOnMouseDown(event);
                    markSubmitIntent();
                    contentWrapperRef.current.scrollIntoView();
                    handleSubmit((data) => action.onSubmit(reset)(data, action.type))();
                  },
                }
              : {
                  onMouseDown: markSubmitIntent,
                  onClick: () => {
                    contentWrapperRef.current.scrollIntoView();
                    handleSubmit((data) => action.onSubmit(reset)(data, action.type))();
                  },
                };

            return (
              <Button
                key={index}
                text={action.label}
                color={action.color}
                data-suppress-blur-preview
                {...submitButtonProps}
                disabled={action?.disabled || disabledSubmit}
              />
            );
          })}
      </Box>

      {allowRemove && Boolean(onRemove) && (
        <Button
          text={removeBtnLabel}
          variant={Variant.SOFT}
          color={Color.DANGER}
          onClick={() => onRemove(reset)}
        />
      )}

      {extraButtons}

      <Button
        text={cancelBtnLabel}
        variant={Variant.SOFT}
        color={Color.NEUTRAL}
        onClick={() => onClose(reset)}
        disabled={disabledCancel}
      />
    </>
  );

  const formContent = isOpen ? (
    <SlidingPanelContent
      buttons={buttons}
      fullWidth={false}
      notificationPosition={notificationPosition}
      withoutNotification={withoutNotification}
      stickyHeader
      stickySxProps={{
        ...stickySxProps,
        notification: {
          marginBottom: '24px',
          ...stickySxProps?.notification,
        },
      }}
    >
      <ContentWrapper ref={contentWrapperRef}>
        {useGridLayout && hasChildren && (
          <Grid container spacing={2}>
            {renderChildren(children)}
          </Grid>
        )}

        {!useGridLayout && hasChildren && (
          <FieldWrapper sx={fieldWrapperSxProps}>{renderChildren(children)}</FieldWrapper>
        )}
      </ContentWrapper>
    </SlidingPanelContent>
  ) : (
    <></>
  );

  if (inline) {
    return formContent;
  }

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={() => !disabledCancel && onClose(reset)}
      title={title}
      subtitle={subtitle}
      size={size}
      interactive={interactive}
      disableClose={disabledCancel}
      disableAutoClose={disableAutoClose}
      drawerSlotAndSlotProps={drawerSlotAndSlotProps}
      level={level}
    >
      {formContent}
    </SlidingPanel>
  );
};

export default FormView;
